<template>
  <node-view-wrapper
    class="image-view"
    :class="{'line-height-1': hasErrorText}"
    :style="{'text-align': textAlign}"
  >
    <div
      ref="imageWrapper"
      :class="{
        'image-view__body--focused': selected && editable,
        'image-view__body--resizing': resizing && editable,
        'image-view__body--no-edit': !editable,
        'image-view__body--error': fetchError || !isSupportedType,
        'image-view__body--fetch-error': fetchError,
      }"
      class="image-view__body"
    >
      <div
        v-if="fetchError || !isSupportedType"
        class="errorWarning"
      >
        <q-icon
          name="warning"
          size="xl"
        />
      </div>

      <universal-tooltip
        :show-tooltip="hasErrorText"
        :text="imageAlt"
      >
        <img
          ref="image"
          :src="src"
          :title="node.attrs.title"
          :alt="imageAlt"
          :width="width"
          :height="height"
          class="image-view__body-image"
          :class="{
            'image-view__body-image--no-edit': !editable,
            'image-view__body-image--unsupport-error': !isSupportedType,
          }"
          @click.self="selectImage"
        >
      </universal-tooltip>

      <div
        v-if="editor.view.editable"
        v-show="selected || resizing"
        class="image-resizer"
      >
        <span
          v-for="direction in resizeDirections"
          :key="direction"
          :class="{
            'image-resizer__handler': !isMobileView,
            [`image-resizer__handler--${direction}`]: !isMobileView,
          }"
          @mousedown="onMouseDown($event, direction)"
        />
      </div>
      <editor-resize-image-menu
        :editable="editable"
        :selected="selected"
        :delete-node="deleteNode"
        :width="resizerState.width"
        :min-width="minWidth"
        :max-width="maxWidth"
        @show-width-menu="recalculateWidthBasedOnImageOffset"
        @change-width="setNewWidth"
      />
    </div>
  </node-view-wrapper>
</template>

<script>
import { Editor, nodeViewProps, NodeViewWrapper } from '@tiptap/vue-3';
import EditorResizeImageMenu from 'components/Editor/EditorResizeImageMenu';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { clamp } from 'lodash';
import { mapState } from 'pinia';
import { NodeSelection } from 'prosemirror-state';
import Interactions from 'src/mixins/Interactions';
import PdfMakeExportMixin from 'src/mixins/PdfMakeExportMixin';
import { network } from 'src/store/network';

const IMAGE_MAX_WIDTH = 1040;
const IMAGE_MIN_WIDTH = 100;

const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'exceed-memory-limit'];

export default {
  components: {
    NodeViewWrapper,
    EditorResizeImageMenu,
    UniversalTooltip,
  },

  mixins: [Interactions, PdfMakeExportMixin],

  props: {
    extension: nodeViewProps.extension,
    selected: nodeViewProps.selected,
    node: nodeViewProps.node,
    getPos: nodeViewProps.getPos,
    updateAttributes: nodeViewProps.updateAttributes,
    deleteNode: nodeViewProps.deleteNode,
    editor: {
      type: Editor,
      required: true,
    },
  },

  data() {
    return {
      resizeDirections: ['left', 'right'],

      resizerState: {
        x: 0,
        width: this.node.attrs.width,
        side: '',
      },

      resizing: false,

      originalSize: {
        width: 0,
      },

      maxWidth: IMAGE_MAX_WIDTH,
      minWidth: IMAGE_MIN_WIDTH,

      showMobileModal: false,
      isSupportedType: true,
      fetchError: false,
    };
  },

  computed: {
    ...mapState(network, ['networkOnline']),

    src() {
      return this.node.attrs.src;
    },

    width() {
      return this.node.attrs.width;
    },

    height() {
      return this.node.attrs.height;
    },

    display() {
      return this.node.attrs.display;
    },

    textAlign() {
      return this.node.attrs.textAlign || '';
    },

    imageDefaultWidth() {
      return this.extension.options.defaultWidth;
    },

    isMobileView() {
      return this.$q.platform.is.mobile || this.$q.screen.lt.sm;
    }, // isMobileView()

    editable() {
      return this.editor.options.editable;
    }, // editable()

    imageAlt() {
      if (this.fetchError) {
        return this.$t('editor.image.imageFetchError');
      }

      if (!this.isSupportedType) {
        return this.$t('editor.image.imageTypeIsNotSupported');
      }

      return this.node.attrs.alt ?? '';
    }, // imageAlt()

    hasErrorText() {
      return this.fetchError || !this.isSupportedType;
    }, // hasErrorText()
  },

  async created() {
    await this.handleBrokenImages();

    this.getImageDimensions(this.src)
      .then((dimensions) => {
        this.originalSize = {
          width: dimensions.width || this.imageDefaultWidth,
        };

        if (this.width === this.imageDefaultWidth) {
          this.updateAttributes(this.originalSize);
        }
      });
  },

  methods: {

    recalculateWidthBasedOnImageOffset() {
      const { offsetWidth } = this.$refs.image;
      if (offsetWidth && offsetWidth < this.node.attrs.width) {
        this.setNewWidth(offsetWidth);
      }
    }, // recalculateWidthBasedOnImageOffset()

    setNewWidth(width) {
      this.resizerState.width = width;
      this.onChangeImageWidth();
    }, // setNewWidth()

    selectImage() {
      const { state } = this.editor.view;
      let { tr } = state;
      const selection = NodeSelection.create(state.doc, this.getPos());
      tr = tr.setSelection(selection);

      this.editor.view.dispatch(tr);

      if (!this.isMobileView) {
        this.recalculateWidthBasedOnImageOffset();
      }
    }, // selectImage()

    onChangeImageWidth() {
      let { width } = this.resizerState;
      width = width || this.minWidth;

      this.updateAttributes({
        width: clamp(width, this.minWidth, this.maxWidth),
      });
    }, // onChangeImageWidth()

    /**
     * @param {MouseEvent} event
     */
    onMouseUp(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.resizing) {
        return;
      }

      this.resizing = false;
      this.resizerState = {
        x: 0,
        width: this.width,
        side: '',
      };
      this.removeMouseEvents();
      this.selectImage();
    }, // onMouseUp()

    /**
     * @param {MouseEvent} event
     * @param {'left' | 'right'} side
     */
    onMouseDown(event, side) {
      event.preventDefault();
      event.stopPropagation();
      this.recalculateWidthBasedOnImageOffset();

      this.resizerState.x = event.clientX;

      const originalWidth = this.originalSize.width;
      let { width } = this.node.attrs;

      if (width) {
        width = width > this.maxWidth ? this.maxWidth : width;
      } else if (!width) {
        width = originalWidth > this.maxWidth ? this.maxWidth : originalWidth;
      } else {
        width = width > this.maxWidth ? this.maxWidth : width;
      }

      this.resizerState.width = width;
      this.resizerState.side = side;
      this.resizing = true;

      this.addMouseEvents();
    }, // onMouseDown()

    /**
     * @param {MouseEvent} event
     */
    onMouseMove(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.resizing) {
        return;
      }

      const { x, width, side } = this.resizerState;
      const updatedX = (event.clientX - x) * (side === 'left' ? -1 : 1);

      this.updateAttributes({
        width: clamp(width + updatedX, this.minWidth, this.maxWidth),
      });
    }, // onMouseMove()

    addMouseEvents() {
      document.addEventListener('mousemove', this.onMouseMove, true);
      document.addEventListener('mouseup', this.onMouseUp, true);
    }, // addMouseEvents()

    removeMouseEvents() {
      document.removeEventListener('mousemove', this.onMouseMove, true);
      document.removeEventListener('mouseup', this.onMouseUp, true);
    }, // removeMouseEvents()

    async handleBrokenImages() {
      if (!this.src) {
        return;
      }

      if (this.networkOnline) {
        try {
          const contentType = await this.getImageType(this.src);
          this.isSupportedType = SUPPORTED_IMAGE_TYPES.includes(contentType);
        } catch (err) {
          this.fetchError = true;
        }

        this.updateImageErrorState();

        // Sometimes we have a fetch error, but the Editor can somehow fetch the image, so we need to ban that image too
        if (this.fetchError) {
          this.notifyNegative(this.$t('editor.image.imageFetchError'));
          return;
        }

        if (!this.isSupportedType) {
          this.notifyNegative(this.$t('editor.image.imageTypeIsNotSupported'));
        }
      }
    },

    updateImageErrorState() {
      const { node, getPos, editor } = this;
      const { state, view } = editor;
      const { tr, doc } = state;

      const pos = getPos ? getPos() : null;

      if (pos === undefined || pos === null || pos < 0 || pos >= doc.content.size) {
        return;
      }

      const newAttrs = { ...node.attrs, failToFetch: this.fetchError, unsupportedImageType: !this.isSupportedType };

      tr.setNodeMarkup(pos, null, newAttrs);

      if (tr.steps.length > 0) {
        view.dispatch(tr);
      }
    },

    getImageType() {
      const isBase64 = this.src.startsWith('data:image');

      if (isBase64) {
        return this.getBase64ImageType(this.src);
      }

      return this.getExternalImageType(this.src);
    },

    async getExternalImageType(url) {
      const response = await fetch(url);
      const reader = response.body.getReader();

      const chunk = await reader.read({ length: 16 });
      const byteArray = new Uint8Array(chunk.value);
      let hexString = '';

      byteArray.forEach((byte) => {
        hexString += byte.toString(16).padStart(2, '0');
      });

      let imageType = '';

      // Use File Magic Numbers to determine the image type
      if (hexString.startsWith('89504e47')) {
        imageType = 'image/png';
      } else if (hexString.startsWith('ffd8')) {
        imageType = 'image/jpeg';
      } else {
        imageType = 'unsupported';
      }

      return imageType;
    },

    getBase64ImageType(url) {
      const separatorIndex = url.indexOf(';');

      if (!separatorIndex) {
        return 'unsupported';
      }

      const base64String = url.replaceAll('=', '');
      const bytes = base64String.length * (3 / 4);

      // We check if the number of bytes exceeds the 1 MB limit
      if (bytes > 1000000) {
        this.notifyNegative(this.$t('common.warningFileTooBig', { maxFileSize: '1 MB' }));
        return 'exceed-memory-limit';
      }

      return url.slice(5, separatorIndex);
    },
  },
};
</script>

<style lang="scss" scoped>
.image-view {
  display: block;
  float: none;
  line-height: 0;
  margin: 12px 0;
  max-width: 100%;
  user-select: none;
  vertical-align: baseline;
  position: relative;

  &--inline {
    margin-left: 12px;
    margin-right: 12px;
  }

  &--block {
    display: block;
  }

  &--left {
    float: left;
    margin-left: 0;
    margin-right: 12px;
  }

  &--right {
    float: right;
    margin-left: 12px;
    margin-right: 0;
  }

  &__body {
    clear: both;
    display: inline-block;
    max-width: 100%;
    outline: transparent solid 2px;
    transition: all .2s ease-in;
    position: relative;

    &:hover {
      outline-color: #ffc83d;
    }

    &--focused,
    &--focused:hover {
      outline-color: $primary;
    }

    &--no-edit {
      &:hover {
        outline-color: white;
      }
    }

    &--error {
      outline: $negative solid 5px;
    }

    &--fetch-error {
      min-width: 400px;
      min-height: 200px;
      padding: 8px;
    }

    &-placeholder {
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: -1;
    }

    &-image {
      cursor: pointer;
      margin: 0;

      &--no-edit {
        cursor: default;
      }

      &--unsupport-error {
        opacity: 0.15;
      }
    }
  }
}

.errorWarning {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: $negative;
}

.line-height-1 {
  line-height: 1;
}

.image-resizer {
  border: 1px solid $primary;
  border-left-width: 3px;
  border-right-width: 3px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;

  &__handler {
    box-sizing: border-box;
    display: block;
    height: 25px;
    width: 4px;
    padding: 0 8px;
    cursor: ew-resize;

    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;

    &--left {
      left: 5px;
    }

    &--right {
      right: 5px;
    }

    &--left:after,
    &--right:after {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);

      content: '';
      background: $primary;
      border: 1px solid $primary;
      border-radius: 2px;
      height: 100%;

      width: 5px;
    }
  }
}
</style>
